"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { GAP_MINUTES } from "@/lib/booking/gap-check";

interface BookingGapWarningModalProps {
  isOpen: boolean;
  onCancel: () => void;
  conflictingShow?: {
    showName: string;
    date: string;
    startTime: string;
    endTime: string;
    timeGap: number;
  };
}

/**
 * Shown when a show would start or end less than 15 minutes from another show
 * in the same studio. It is a hard stop: there is no way to book past it, and
 * the staff booking routes refuse such bookings too (lib/booking/gap-check.ts).
 */
export function BookingGapWarningModal({
  isOpen,
  onCancel,
  conflictingShow,
}: BookingGapWarningModalProps) {
  // Only render if we have valid data
  if (!conflictingShow) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <DialogTitle>Not enough time between shows</DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription asChild>
          <div className="space-y-3 text-base">
            <p className="text-sm text-gray-900">
              <strong>{conflictingShow.showName}</strong> runs{" "}
              <strong>
                {conflictingShow.startTime}–{conflictingShow.endTime}
              </strong>{" "}
              in this studio.
            </p>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-700">
                {conflictingShow.timeGap === 0
                  ? "The shows overlap"
                  : `Only ${conflictingShow.timeGap} minutes between the shows`}
              </p>
              <p className="mt-1 text-xs text-red-600">
                Shows need at least {GAP_MINUTES} minutes between them for venue
                prep and cleanup.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              Please choose another time or studio.
            </p>
          </div>
        </DialogDescription>

        <DialogFooter>
          <Button onClick={onCancel} className="w-full">
            Choose another time
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
